import React, { useCallback, useEffect, useState } from 'react';
import { CalendarClock, ArrowRight } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { t, formatDate, formatTime } from '../i18n';
import { dayKey } from '../lib/practiceTime';
import { locationForMove, placeLabel } from '../lib/availability';
import { Button, Modal, Field, Input, Alert, Spinner } from './ui';

/**
 * Moving a session to another time.
 *
 * Offers the professional's free slots for the chosen day rather than a bare
 * time field, so a time that would be refused — taken, blocked, outside working
 * hours — cannot be picked in the first place. The same list the booking grid
 * draws from, so the two can never disagree about what is free.
 *
 * Each slot says where it is. A session moved stays online, or in person, when
 * the new time allows that, and otherwise takes what the new time offers
 * (migration 0044).
 *
 * The move itself goes through reschedule_appointment, which keeps the billing
 * snapshot and updates the calendar entry in place instead of cancelling it.
 */

const RescheduleModal = ({ appointment, therapistId, patientName, onClose, onDone }) => {
  // The session's day on the practice's calendar: the slots asked for are laid
  // out on it, whatever clock the browser is on.
  const [day, setDay] = useState(() => dayKey(appointment.starts_at));
  const [slots, setSlots] = useState([]);
  const [onlineLocation, setOnlineLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!therapistId) return;
    setLoading(true);
    setError(null);
    const [{ data, error: slotError }, { data: online }] = await Promise.all([
      supabase.rpc('available_slots', { p_therapist_id: therapistId, p_from: day, p_to: day }),
      supabase.from('locations').select('id, name').eq('is_online', true).eq('is_active', true)
        .order('created_at').limit(1),
    ]);
    setLoading(false);
    if (slotError) { setError(slotError.message); return; }
    setOnlineLocation(online?.[0] ?? null);

    const now = Date.now();
    setSlots((data ?? []).filter((slot) =>
      // The slot this session already occupies reads as taken by itself, so it
      // is dropped rather than offered as somewhere to move to.
      slot.slot_start !== appointment.starts_at
      && new Date(slot.slot_start).getTime() > now));
  }, [therapistId, day, appointment.starts_at]);

  useEffect(() => { load(); }, [load]);

  const move = async (slot) => {
    setBusy(true);
    setError(null);
    const { error: moveError } = await supabase.rpc('reschedule_appointment', {
      p_appointment_id: appointment.id,
      p_starts_at: slot.slot_start,
      p_location_id: locationForMove({ currentOnline: appointment.locations?.is_online, slot, onlineLocation }),
    });
    setBusy(false);
    if (moveError) { setError(moveError.message); return; }
    onDone?.();
    onClose();
  };

  const free = slots.filter((slot) => slot.is_free);

  return (
    <Modal
      open
      title={t('appointments.reschedule')}
      onClose={onClose}
      footer={<Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>}
    >
      <div className="space-y-4">
        <div className="rounded-lg bg-slate-50 p-4 text-sm">
          <p className="font-medium text-slate-900">{patientName}</p>
          <p className="mt-1 flex items-center gap-2 text-slate-600">
            <CalendarClock size={14} className="shrink-0 text-slate-400" />
            <span className="first-letter:uppercase">
              {formatDate(appointment.starts_at, { weekday: 'long', day: 'numeric', month: 'long' })}
              {', '}{formatTime(appointment.starts_at)}
              {appointment.locations?.name ? ` · ${appointment.locations.name}` : ''}
            </span>
          </p>
        </div>

        <Field label={t('reschedule.newDay')}>
          <Input type="date" value={day} onChange={(event) => setDay(event.target.value)} />
        </Field>

        {error && <Alert tone="danger">{error}</Alert>}

        {loading ? <Spinner /> : free.length === 0 ? (
          <Alert tone="info">{t('reschedule.noSlots')}</Alert>
        ) : (
          <div>
            <p className="mb-2 text-sm text-slate-600">{t('reschedule.pick')}</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {free.map((slot) => (
                <button
                  key={slot.slot_start}
                  disabled={busy}
                  onClick={() => move(slot)}
                  className="flex h-12 flex-col items-center justify-center overflow-hidden rounded bg-indigo-50 px-2 text-sm font-medium text-indigo-700 transition hover:bg-indigo-100 disabled:opacity-50"
                >
                  <span className="leading-tight">{formatTime(slot.slot_start)}</span>
                  <span className="block w-full truncate text-center text-[11px] font-normal leading-tight opacity-80">
                    {placeLabel(slot)}
                  </span>
                </button>
              ))}
            </div>
            <p className="mt-3 flex items-start gap-1.5 text-xs text-slate-500">
              <ArrowRight size={12} className="mt-0.5 shrink-0" />
              {t('reschedule.keepsPrice')}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RescheduleModal;
